import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Plus, Trash, PencilSimple, EnvelopeSimple, WhatsappLogo, PhoneCall, ChatCircle,
  Phone, UsersThree, Envelope, NotePencil, MagnifyingGlass, Paperclip, Image as ImageIcon,
  DownloadSimple, X, ChatCircleText,
} from '@phosphor-icons/react';

/**
 * Communications Panel (Phase 910)
 * ------------------------------------------------------------------
 * Per-building communications timeline. Fully isolated — every request is
 * scoped to `buildingId`. No coupling to other modules.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { v: 'email', label: 'Email', Icon: EnvelopeSimple, color: 'text-sky-600' },
  { v: 'whatsapp', label: 'WhatsApp', Icon: WhatsappLogo, color: 'text-emerald-600' },
  { v: 'viber', label: 'Viber', Icon: PhoneCall, color: 'text-purple-600' },
  { v: 'sms', label: 'SMS', Icon: ChatCircle, color: 'text-blue-600' },
  { v: 'phone_call', label: 'Phone Call', Icon: Phone, color: 'text-orange-600' },
  { v: 'meeting', label: 'Meeting', Icon: UsersThree, color: 'text-indigo-600' },
  { v: 'letter', label: 'Letter', Icon: Envelope, color: 'text-amber-700' },
  { v: 'note', label: 'Manual Note', Icon: NotePencil, color: 'text-zinc-600' },
];

const RECIPIENT_TYPES = [
  { v: 'management_committee', el: 'Επιτροπή Διαχείρισης', en: 'Management Committee' },
  { v: 'owner', el: 'Ιδιοκτήτης', en: 'Owner' },
  { v: 'tenant', el: 'Ενοικιαστής', en: 'Tenant' },
  { v: 'supplier', el: 'Προμηθευτής', en: 'Supplier' },
  { v: 'all_owners', el: 'Όλοι οι Ιδιοκτήτες', en: 'All Owners' },
  { v: 'all_tenants', el: 'Όλοι οι Ενοικιαστές', en: 'All Tenants' },
  { v: 'everyone', el: 'Όλοι', en: 'Everyone' },
  { v: 'other', el: 'Άλλο', en: 'Other' },
];

const typeMeta = (v) => TYPES.find(t => t.v === v) || TYPES[TYPES.length - 1];

export default function CommunicationsPanel({ buildingId, apartments = [], language = 'en', isAdmin = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ type: '', recipient_type: '', tag: '', date_from: '', date_to: '', q: '' });
  const [dialog, setDialog] = useState(null); // {mode:'create'|'edit', doc}
  const [viewer, setViewer] = useState(null); // {url, filename}

  const isEl = language === 'el';
  const label = (el, en) => (isEl ? el : en);

  const reload = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const r = await axios.get(`${API}/buildings/${buildingId}/communications?${params.toString()}`);
      setItems(r.data.communications || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { if (buildingId) reload(); /* eslint-disable-next-line */ }, [buildingId]);

  const applyFilters = () => reload();
  const clearFilters = () => {
    setFilters({ type: '', recipient_type: '', tag: '', date_from: '', date_to: '', q: '' });
    setTimeout(reload, 0);
  };

  const openCreate = () => setDialog({
    mode: 'create',
    doc: { communication_type: 'email', recipient_type: 'owner', recipient_id: '',
           recipient_name: '', subject: '', message: '', tags: [], notes: '',
           _newTag: '', _newFiles: [] },
  });

  const openEdit = async (comm) => {
    setDialog({ mode: 'edit', doc: { ...comm, _newTag: '', _newFiles: [] } });
  };

  const save = async () => {
    const d = dialog.doc;
    const body = {
      communication_type: d.communication_type,
      recipient_type: d.recipient_type,
      recipient_id: d.recipient_id, recipient_name: d.recipient_name,
      subject: d.subject, message: d.message, tags: d.tags || [], notes: d.notes,
    };
    try {
      let commId = d.comm_id;
      if (dialog.mode === 'create') {
        const r = await axios.post(`${API}/buildings/${buildingId}/communications`, body);
        commId = r.data.comm_id;
      } else {
        await axios.put(`${API}/buildings/${buildingId}/communications/${commId}`, body);
      }
      // Upload any newly added files.
      for (const f of (d._newFiles || [])) {
        await axios.post(`${API}/buildings/${buildingId}/communications/${commId}/attachments`, {
          filename: f.name, content_type: f.type || 'application/octet-stream', data_b64: f.data,
        });
      }
      toast.success(label('Αποθηκεύτηκε', 'Saved'));
      setDialog(null);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (comm) => {
    if (!window.confirm(label('Διαγραφή;', 'Delete this record?'))) return;
    try {
      await axios.delete(`${API}/buildings/${buildingId}/communications/${comm.comm_id}`);
      toast.success(label('Διαγράφηκε', 'Deleted'));
      reload();
    } catch (e) { toast.error('Delete failed'); }
  };

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    const converted = [];
    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`${f.name}: ${label('>5MB, παραλείπεται', '>5MB skipped')}`);
        continue;
      }
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res((r.result || '').toString().split(',')[1] || '');
        r.onerror = rej;
        r.readAsDataURL(f);
      });
      converted.push({ name: f.name, type: f.type, size: f.size, data: b64 });
    }
    setDialog(dl => dl ? { ...dl, doc: { ...dl.doc, _newFiles: [...(dl.doc._newFiles || []), ...converted] } } : dl);
    e.target.value = '';
  };

  const removeExistingAtt = async (commId, attId) => {
    if (!window.confirm(label('Διαγραφή αρχείου;', 'Remove attachment?'))) return;
    try {
      await axios.delete(`${API}/buildings/${buildingId}/communications/${commId}/attachments/${attId}`);
      // Refresh dialog and list
      const r = await axios.get(`${API}/buildings/${buildingId}/communications/${commId}`);
      setDialog(dl => dl ? { ...dl, doc: { ...dl.doc, ...r.data, _newFiles: dl.doc._newFiles || [] } } : dl);
      reload();
    } catch (e) { toast.error('Delete attachment failed'); }
  };

  const attUrl = (commId, attId) =>
    `${API}/buildings/${buildingId}/communications/${commId}/attachments/${attId}`;

  const filtered = useMemo(() => items, [items]);

  return (
    <div className="space-y-4" data-testid="communications-panel">
      {/* Toolbar / filters */}
      <div className="bg-white border border-zinc-200 p-3 space-y-2" data-testid="comm-filters">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[220px] relative">
            <MagnifyingGlass size={13} className="absolute left-2 top-2.5 text-zinc-400" />
            <Input placeholder={label('Αναζήτηση σε θέμα/μήνυμα/παραλήπτη...', 'Search subject / message / recipient...')}
                   value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })}
                   className="rounded-none pl-7 text-sm" data-testid="comm-search"
                   onKeyDown={e => e.key === 'Enter' && applyFilters()} />
          </div>
          <Select value={filters.type} onValueChange={v => setFilters({ ...filters, type: v === '__all' ? '' : v })}>
            <SelectTrigger className="rounded-none max-w-[160px] text-xs" data-testid="comm-filter-type">
              <SelectValue placeholder={label('Τύπος', 'Type')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">{label('Όλοι οι Τύποι', 'All Types')}</SelectItem>
              {TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.recipient_type} onValueChange={v => setFilters({ ...filters, recipient_type: v === '__all' ? '' : v })}>
            <SelectTrigger className="rounded-none max-w-[180px] text-xs" data-testid="comm-filter-recipient">
              <SelectValue placeholder={label('Παραλήπτης', 'Recipient')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">{label('Όλοι', 'All Recipients')}</SelectItem>
              {RECIPIENT_TYPES.map(r => <SelectItem key={r.v} value={r.v}>{isEl ? r.el : r.en}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })}
                 className="rounded-none max-w-[150px] text-xs" data-testid="comm-filter-from" />
          <Input type="date" value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })}
                 className="rounded-none max-w-[150px] text-xs" data-testid="comm-filter-to" />
          <Input placeholder={label('Ετικέτα', 'Tag')} value={filters.tag} onChange={e => setFilters({ ...filters, tag: e.target.value })}
                 className="rounded-none max-w-[120px] text-xs" data-testid="comm-filter-tag" />
          <Button size="sm" variant="outline" className="rounded-none text-xs" onClick={applyFilters} data-testid="comm-filter-apply">
            {label('Εφαρμογή', 'Apply')}
          </Button>
          <Button size="sm" variant="outline" className="rounded-none text-xs" onClick={clearFilters} data-testid="comm-filter-clear">
            {label('Εκκαθάριση', 'Clear')}
          </Button>
          {isAdmin && (
            <Button size="sm" className="bg-zinc-950 rounded-none" onClick={openCreate} data-testid="comm-new">
              <Plus size={14} className="mr-1" />{label('Νέα Επικοινωνία', 'New Communication')}
            </Button>
          )}
        </div>
      </div>

      {/* Timeline */}
      {loading && <div className="text-xs text-zinc-500">Loading…</div>}
      {!loading && filtered.length === 0 && (
        <div className="p-6 text-center text-sm text-zinc-500 border border-dashed border-zinc-300">
          <ChatCircleText size={20} className="mx-auto mb-2 text-zinc-400" />
          {label('Δεν υπάρχουν καταχωρήσεις για τα επιλεγμένα φίλτρα.', 'No communications yet.')}
        </div>
      )}
      <div className="space-y-2">
        {filtered.map(c => {
          const meta = typeMeta(c.communication_type);
          const Icon = meta.Icon;
          const dt = (c.created_at || '').slice(0, 16).replace('T', ' ');
          const nPhotos = (c.photos || []).length;
          const nAtts = (c.attachments || []).length;
          return (
            <div key={c.comm_id} className="bg-white border border-zinc-200 p-3 hover:bg-zinc-50 flex gap-3 items-start" data-testid={`comm-card-${c.comm_id}`}>
              <div className={`p-2 border border-zinc-200 ${meta.color}`}><Icon size={18} /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase font-semibold text-zinc-500">{meta.label}</span>
                  <span className="text-[11px] text-zinc-400">{dt}</span>
                  {c.recipient_type && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-700">
                      {(RECIPIENT_TYPES.find(r => r.v === c.recipient_type) || {})[isEl ? 'el' : 'en'] || c.recipient_type}
                    </span>
                  )}
                  {c.recipient_name && <span className="text-xs text-zinc-700 truncate">→ {c.recipient_name}</span>}
                </div>
                {c.subject && <div className="text-sm font-medium text-zinc-900 mt-1 truncate">{c.subject}</div>}
                {c.message && <div className="text-xs text-zinc-600 mt-0.5 line-clamp-2 whitespace-pre-wrap">{c.message}</div>}
                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-zinc-500">
                  {nPhotos > 0 && <span><ImageIcon size={11} className="inline mr-1" />{nPhotos}</span>}
                  {nAtts > 0 && <span><Paperclip size={11} className="inline mr-1" />{nAtts}</span>}
                  {(c.tags || []).map(t => <span key={t} className="px-1 bg-amber-50 text-amber-800">#{t}</span>)}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => openEdit(c)} className="p-1 text-zinc-400 hover:text-zinc-800" title={label('Επεξεργασία', 'Edit')} data-testid={`comm-edit-${c.comm_id}`}>
                  <PencilSimple size={14} />
                </button>
                <button onClick={() => remove(c)} className="p-1 text-zinc-400 hover:text-red-600" title={label('Διαγραφή', 'Delete')} data-testid={`comm-delete-${c.comm_id}`}>
                  <Trash size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-w-2xl rounded-none max-h-[85vh] overflow-y-auto" data-testid="comm-dialog">
          <DialogHeader>
            <DialogTitle>
              {dialog?.mode === 'create'
                ? label('Νέα Επικοινωνία', 'New Communication')
                : label('Επεξεργασία Επικοινωνίας', 'Edit Communication')}
            </DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{label('Τύπος', 'Type')}</Label>
                  <Select value={dialog.doc.communication_type} onValueChange={v => setDialog({ ...dialog, doc: { ...dialog.doc, communication_type: v } })}>
                    <SelectTrigger className="rounded-none" data-testid="comm-dlg-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{label('Τύπος Παραλήπτη', 'Recipient Type')}</Label>
                  <Select value={dialog.doc.recipient_type || ''} onValueChange={v => setDialog({ ...dialog, doc: { ...dialog.doc, recipient_type: v } })}>
                    <SelectTrigger className="rounded-none" data-testid="comm-dlg-rtype"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RECIPIENT_TYPES.map(r => <SelectItem key={r.v} value={r.v}>{isEl ? r.el : r.en}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {(dialog.doc.recipient_type === 'owner' || dialog.doc.recipient_type === 'tenant') && apartments.length > 0 && (
                <div>
                  <Label className="text-xs">{label('Διαμέρισμα (auto-fill)', 'Apartment (auto-fill)')}</Label>
                  <Select value={dialog.doc.recipient_id || ''} onValueChange={v => {
                    const apt = apartments.find(a => a.apartment_id === v);
                    setDialog({ ...dialog, doc: { ...dialog.doc, recipient_id: v,
                      recipient_name: dialog.doc.recipient_type === 'owner'
                        ? (apt?.owner_name || dialog.doc.recipient_name)
                        : (apt?.tenant_name || dialog.doc.recipient_name) } });
                  }}>
                    <SelectTrigger className="rounded-none" data-testid="comm-dlg-apt"><SelectValue placeholder={label('Επίλεξε...', 'Pick...')} /></SelectTrigger>
                    <SelectContent>
                      {apartments.map(a => (
                        <SelectItem key={a.apartment_id} value={a.apartment_id}>
                          {a.apartment_number}{a.owner_name ? ` — ${a.owner_name}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs">{label('Όνομα Παραλήπτη', 'Recipient Name')}</Label>
                <Input value={dialog.doc.recipient_name} onChange={e => setDialog({ ...dialog, doc: { ...dialog.doc, recipient_name: e.target.value } })} className="rounded-none" data-testid="comm-dlg-rname" />
              </div>
              <div>
                <Label className="text-xs">{label('Θέμα', 'Subject')}</Label>
                <Input value={dialog.doc.subject} onChange={e => setDialog({ ...dialog, doc: { ...dialog.doc, subject: e.target.value } })} className="rounded-none" data-testid="comm-dlg-subject" />
              </div>
              <div>
                <Label className="text-xs">{label('Μήνυμα / Περίληψη', 'Message / Summary')}</Label>
                <Textarea rows={5} value={dialog.doc.message} onChange={e => setDialog({ ...dialog, doc: { ...dialog.doc, message: e.target.value } })} className="rounded-none text-sm" data-testid="comm-dlg-message" />
              </div>
              <div>
                <Label className="text-xs">{label('Ετικέτες', 'Tags')}</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(dialog.doc.tags || []).map(t => (
                    <span key={t} className="px-2 py-0.5 bg-amber-50 text-amber-800 text-xs flex items-center gap-1">
                      #{t}
                      <button type="button" onClick={() => setDialog({ ...dialog, doc: { ...dialog.doc, tags: dialog.doc.tags.filter(x => x !== t) } })}><X size={10} /></button>
                    </span>
                  ))}
                  <Input value={dialog.doc._newTag} placeholder="new tag ↵" onChange={e => setDialog({ ...dialog, doc: { ...dialog.doc, _newTag: e.target.value } })}
                         onKeyDown={e => {
                           if (e.key === 'Enter' && dialog.doc._newTag.trim()) {
                             e.preventDefault();
                             setDialog({ ...dialog, doc: { ...dialog.doc, tags: [...(dialog.doc.tags || []), dialog.doc._newTag.trim()], _newTag: '' } });
                           }
                         }}
                         className="rounded-none text-xs max-w-[140px]" data-testid="comm-dlg-tag-input" />
                </div>
              </div>
              <div>
                <Label className="text-xs">{label('Εσωτ. Σημειώσεις', 'Internal Notes')}</Label>
                <Textarea rows={2} value={dialog.doc.notes} onChange={e => setDialog({ ...dialog, doc: { ...dialog.doc, notes: e.target.value } })} className="rounded-none text-xs" data-testid="comm-dlg-notes" />
              </div>

              {/* Existing attachments (edit mode) */}
              {dialog.mode === 'edit' && (
                <div>
                  <Label className="text-xs">{label('Επισυναπτόμενα', 'Attachments')}</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {[...(dialog.doc.photos || []), ...(dialog.doc.attachments || [])].map(a => {
                      const url = attUrl(dialog.doc.comm_id, a.att_id);
                      const isImg = (a.content_type || '').startsWith('image/');
                      return (
                        <div key={a.att_id} className="border border-zinc-200 p-1 text-[10px] flex flex-col items-center gap-1" data-testid={`comm-dlg-att-${a.att_id}`}>
                          {isImg
                            ? <img src={url} alt={a.filename} className="h-16 w-full object-cover cursor-pointer" onClick={() => setViewer({ url, filename: a.filename })} />
                            : <Paperclip size={20} className="text-zinc-500 mt-2" />}
                          <div className="truncate w-full text-center" title={a.filename}>{a.filename}</div>
                          <div className="flex gap-1">
                            <a href={url} target="_blank" rel="noreferrer" className="p-0.5 hover:text-zinc-800"><DownloadSimple size={11} /></a>
                            <button type="button" onClick={() => removeExistingAtt(dialog.doc.comm_id, a.att_id)} className="p-0.5 hover:text-red-600"><Trash size={11} /></button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Newly-added-but-not-yet-uploaded files preview */}
              {(dialog.doc._newFiles || []).length > 0 && (
                <div>
                  <Label className="text-xs text-emerald-700">{label('Προς μεταφόρτωση', 'To be uploaded')}</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {dialog.doc._newFiles.map((f, i) => (
                      <span key={i} className="text-[10px] bg-emerald-50 text-emerald-800 px-2 py-0.5">{f.name}</span>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs">{label('Προσθήκη Αρχείων', 'Add Files')} <span className="text-[10px] text-zinc-400">(max 5MB each)</span></Label>
                <Input type="file" multiple accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain"
                       onChange={onPickFiles} className="rounded-none" data-testid="comm-dlg-files" />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDialog(null)} className="rounded-none" data-testid="comm-dlg-cancel">
                  {label('Ακύρωση', 'Cancel')}
                </Button>
                <Button onClick={save} className="bg-zinc-950 rounded-none" data-testid="comm-dlg-save">
                  {label('Αποθήκευση', 'Save')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image viewer */}
      <Dialog open={!!viewer} onOpenChange={v => !v && setViewer(null)}>
        <DialogContent className="max-w-3xl rounded-none" data-testid="comm-viewer">
          {viewer && (
            <div>
              <div className="text-xs mb-2">{viewer.filename}</div>
              <img src={viewer.url} alt={viewer.filename} className="max-h-[75vh] mx-auto" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
